import * as Dialog from "@radix-ui/react-dialog";
import { X, Edit, Trash2, Power } from "lucide-react";

const MODAL_TYPES = {
  ENABLE_DISABLE: 'enable_disable',
  EDIT: 'edit',
  DELETE: 'delete',
  CONFIRM: 'confirm',
  CUSTOM: 'custom'
};

const MODAL_CONFIGS = {
  [MODAL_TYPES.ENABLE_DISABLE]: {
    icon: Power,
    getTitle: (data) => `${data?.status ? 'Disable' : 'Enable'} ${data?.entityType || 'Item'}`,
    getMessage: (data) => `Are you sure you want to ${data?.status ? 'disable' : 'enable'} this ${data?.entityType?.toLowerCase() || 'item'}?`,
    confirmText: 'Confirm',
    confirmClass: 'bg-blue-600 hover:bg-blue-700',
  },
  [MODAL_TYPES.DELETE]: {
    icon: Trash2,
    getTitle: (data) => `Delete ${data?.entityType || 'Item'}`,
    getMessage: (data) => `Are you sure you want to delete this ${data?.entityType?.toLowerCase() || 'item'}? This action cannot be undone.`,
    confirmText: 'Delete',
    confirmClass: 'bg-red-600 hover:bg-red-700',
  },
  [MODAL_TYPES.EDIT]: {
    icon: Edit,
    getTitle: (data) => `Edit ${data?.entityType || 'Item'}`,
    getMessage: () => null, // No message for edit, will show form
    confirmText: 'Save Changes',
    confirmClass: 'bg-green-600 hover:bg-green-700',
  },
  [MODAL_TYPES.CONFIRM]: {
    icon: null,
    getTitle: (data) => `Confirm Action`,
    getMessage: (data) => `Are you sure you want to proceed with this action?`,
    confirmText: 'Yes, Proceed',
    confirmClass: 'bg-purple-600 hover:bg-purple-700',
  }
};

export default function ReusableModal({
  open,
  onOpenChange,
  type = MODAL_TYPES.CUSTOM,
  title,
  message: customMessage,
  data,
  onConfirm,
  onCancel,
  children,
  confirmText,
  cancelText = "Cancel",
  showActions = true,
  loading = false,
  size = "default" // "default" | "large"
}) {
  const config = MODAL_CONFIGS[type];
  const Icon = config?.icon;

  const modalTitle = title || (config?.getTitle ? config.getTitle(data) : '');
  const message = customMessage || (config?.getMessage ? config.getMessage(data) : null);
  const confirmButtonText = confirmText || config?.confirmText || 'Confirm';
  const confirmButtonClass = config?.confirmClass || 'bg-blue-600 hover:bg-blue-700';

  const sizeClasses = {
    default: 'max-w-lg',
    large: 'max-w-4xl'
  };
  
  const modalSizeClass = sizeClasses[size] || sizeClasses.default;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(data);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className={`fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] ${modalSizeClass} max-h-[90vh] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-lg focus:outline-none flex flex-col sm:w-full`}>
          {/* Sticky Header */}
          <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center space-x-2">
              {Icon && <Icon className="h-5 w-5 text-gray-600" />}
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {modalTitle}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button
                className="rounded-md p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 pt-4">
            {message && (
              <p className="text-gray-700 mb-4">{message}</p>
            )}
            {children}
          </div>

          {/* Sticky Footer Actions */}
          {showActions && (
            <div className="flex justify-end space-x-2 p-6 pt-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={handleCancel}
                disabled={loading}
                className="rounded border px-4 py-2 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelText}
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                className={`rounded px-4 py-2 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${confirmButtonClass}`}
              >
                {loading ? 'Processing...' : confirmButtonText}
              </button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export { MODAL_TYPES };